import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { canSeeAdmin } from '../lib/permissions';

const dateTimeFmt = new Intl.DateTimeFormat('en-US', {
  month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit',
});
const human = (v='') => String(v).replaceAll('_',' ').replace(/\b\w/g,c=>c.toUpperCase());
const raw = v => v == null ? 'Not set' : typeof v === 'string' ? v : JSON.stringify(v,null,2);
const number = v => Number(v || 0);
const prettyNumber = v => Number.isInteger(number(v)) ? String(number(v)) : number(v).toLocaleString('en-US',{maximumFractionDigits:2});

const sourceLabels = {
  today_counter: 'Today',
  missed_activity: 'Missed Activity',
  manual: 'Manual',
  google_forms: 'Google Forms',
  calendly: 'Calendly',
  system: 'System',
};
const sourceLabel = v => sourceLabels[v] || human(v || 'system');

const activityLabels = {
  prospect_contacted: { singular: 'Outbound DM Sent', plural: 'Outbound DMs Sent', metric: 'outbound DM' },
  response: { singular: 'Response Received', plural: 'Responses Received', metric: 'response' },
  conversation_started: { singular: 'Conversation Started', plural: 'Conversations Started', metric: 'conversation' },
  lead_identified: { singular: 'Lead Identified', plural: 'Leads Identified', metric: 'Lead' },
  offered: { singular: 'Call Offered', plural: 'Calls Offered', metric: 'offer' },
  form_sent: { singular: 'Qualification Form Sent', plural: 'Qualification Forms Sent', metric: 'form' },
  qualified: { singular: 'Lead Qualified', plural: 'Leads Qualified', metric: 'qualified Lead' },
  not_qualified: { singular: 'Lead Marked Not Qualified', plural: 'Leads Marked Not Qualified', metric: 'Lead' },
  booked: { singular: 'Call Booked', plural: 'Calls Booked', metric: 'booking' },
  showed: { singular: 'Call Showed', plural: 'Calls Showed', metric: 'show' },
  no_show: { singular: 'Call No Show', plural: 'Call No Shows', metric: 'no-show' },
  closed_won: { singular: 'Closed Won', plural: 'Closed Won', metric: 'win' },
  closed_lost: { singular: 'Closed Lost', plural: 'Closed Lost', metric: 'loss' },
  payment_received: { singular: 'Payment Received', plural: 'Payments Received', metric: 'payment' },
  followup_sent: { singular: 'Follow-up Sent', plural: 'Follow-ups Sent', metric: 'follow-up' },
  action_reverted: { singular: 'CRM Action Reverted', plural: 'CRM Actions Reverted', metric: 'revert' },
};

function activityInfo(row){
  if(row?.entity_type !== 'activity_events' || !row.after_value?.event_type) return null;
  const event = row.after_value;
  const qty = number(event.numeric_value);
  const base = activityLabels[event.event_type] || { singular: human(event.event_type), plural: human(event.event_type), metric: human(event.event_type).toLowerCase() };
  const correction = qty < 0 || event.metadata?.correction === true;
  const title = correction
    ? `${base.singular.replace(/ Sent$| Received$| Started$| Identified$/,'')} Count Corrected`
    : Math.abs(qty) === 1 ? base.singular : base.plural;
  const sign = qty > 0 ? '+' : qty < 0 ? '−' : '';
  const value = `${sign}${prettyNumber(Math.abs(qty))} ${base.metric}${Math.abs(qty) === 1 ? '' : 's'}`;
  return { title, value, motion: event.motion || null, occurredAt: event.occurred_at || null, recordedAt: event.created_at || row.created_at, correction };
}

function changedFields(row){
  if(row.action !== 'update') return [];
  if(row.field_name) return row.field_name.split(',').map(x=>x.trim()).filter(Boolean);
  const before = row.before_value || {};
  const after = row.after_value || {};
  return [...new Set([...Object.keys(before),...Object.keys(after)])]
    .filter(k=>k !== 'updated_at' && JSON.stringify(before[k]) !== JSON.stringify(after[k]));
}

function simpleValue(v){
  if(v == null) return 'Not set';
  if(typeof v === 'boolean') return v ? 'Yes' : 'No';
  if(typeof v === 'object') return JSON.stringify(v);
  return human(String(v));
}

function groupTitle(rows){
  const activity = rows.map(activityInfo).find(Boolean);
  if(activity) return activity.title;
  if(rows.some(r=>r.action==='revert'||r.reverted_event_id)) return 'CRM Action Reverted';
  const member=rows.find(r=>r.entity_type==='workspace_members');
  if(member){
    const fields=changedFields(member);
    if(fields.includes('role')) return 'Team Member Role Changed';
    if(fields.includes('active')) return member.after_value?.active ? 'Team Member Activated' : 'Team Member Deactivated';
    if(member.action==='insert') return 'Team Member Added';
    return 'Team Membership Changed';
  }
  if(rows.some(r=>r.entity_type==='integration_connections')) return 'Integration Changed';
  if(rows.some(r=>r.entity_type==='workspaces')) return 'Workspace Changed';
  const lead=rows.find(r=>r.entity_type==='leads');
  if(lead?.action==='insert') return 'Lead Created';
  if(lead?.action==='update'){
    const fields=changedFields(lead);
    if(fields.includes('validity')) return lead.after_value?.validity==='not_a_lead' ? 'Marked Not a Lead' : 'Restored as Lead';
    if(fields.includes('archive_state')) return lead.after_value?.archive_state==='archived' ? 'Lead Archived' : lead.after_value?.archive_state==='review' ? 'Archive Review Requested' : 'Lead Reactivated';
  }
  const cycle=rows.find(r=>r.entity_type==='sales_cycles'&&r.action==='update');
  if(cycle){
    const fields=changedFields(cycle);
    if(fields.includes('qualification_status')) return `Qualification changed to ${human(cycle.after_value?.qualification_status)}`;
    if(fields.includes('sales_outcome')) return cycle.after_value?.sales_outcome==='closed_won' ? 'Closed Won' : cycle.after_value?.sales_outcome==='closed_lost' ? 'Closed Lost' : 'Sales Outcome Changed';
    if(fields.includes('journey_stage')) return `Journey changed to ${human(cycle.after_value?.journey_stage)}`;
  }
  const row=rows.find(r=>r.entity_type!=='activity_events')||rows[0];
  return `${human(row.action)} ${human(row.entity_type).replace(/s$/,'')}`;
}

function groupValue(rows){
  const activity=rows.map(activityInfo).find(Boolean);
  if(activity) return { primary:activity.value, secondary:activity.motion ? human(activity.motion) : '' };
  const cycle=rows.find(r=>r.entity_type==='sales_cycles'&&r.action==='update');
  if(cycle){
    const fields=changedFields(cycle);
    const field=['qualification_status','sales_outcome','journey_stage'].find(f=>fields.includes(f));
    if(field) return { primary:`${simpleValue(cycle.before_value?.[field])} → ${simpleValue(cycle.after_value?.[field])}`, secondary:human(field) };
  }
  const member=rows.find(r=>r.entity_type==='workspace_members'&&r.action==='update');
  if(member){
    const field=changedFields(member)[0];
    if(field) return { primary:`${simpleValue(member.before_value?.[field])} → ${simpleValue(member.after_value?.[field])}`, secondary:human(field) };
  }
  return { primary:'', secondary:'' };
}

function Fact({label,children,accent=false}){
  return <div className="audit-fact"><span>{label}</span><strong className={accent?'audit-accent':''}>{children}</strong></div>;
}

function TechnicalEvidence({row}){
  const info=activityInfo(row);
  const fields=changedFields(row);
  return <details className="audit-tech">
    <summary>Technical evidence</summary>
    <div className="audit-tech-body">
      <dl className="audit-tech-grid">
        <dt>Entity</dt><dd>{row.entity_type}</dd>
        <dt>Database action</dt><dd>{row.action.toUpperCase()}</dd>
        {info&&<><dt>Technical event</dt><dd>{row.after_value?.event_type}</dd></>}
        {info?.motion&&<><dt>Motion</dt><dd>{info.motion}</dd></>}
        {fields.length>0&&<><dt>Changed field{fields.length===1?'':'s'}</dt><dd>{fields.join(', ')}</dd></>}
        <dt>Action group</dt><dd>{row.action_group_id||'Not set'}</dd>
        <dt>Actor ID</dt><dd>{row.actor_id||'System'}</dd>
        {row.reverted_event_id&&<><dt>Reverted event</dt><dd>{row.reverted_event_id}</dd></>}
      </dl>
      <div className="audit-raw-grid">
        {row.action!=='insert'&&<details className="audit-raw"><summary>Raw before record</summary><pre>{raw(row.before_value)}</pre></details>}
        <details className="audit-raw"><summary>{row.action==='delete'?'Raw deleted record':'Raw persisted record'}</summary><pre>{raw(row.after_value ?? row.before_value)}</pre></details>
      </div>
    </div>
  </details>;
}

function AuditRecord({row}){
  const info=activityInfo(row);
  const fields=changedFields(row);
  if(info){
    const occurred=info.occurredAt ? dateTimeFmt.format(new Date(info.occurredAt)) : 'Not set';
    const recorded=info.recordedAt ? dateTimeFmt.format(new Date(info.recordedAt)) : dateTimeFmt.format(new Date(row.created_at));
    return <div className="audit-record-business">
      <div className="audit-facts-grid">
        <Fact label="Occurred at">{occurred}</Fact>
        <Fact label="Recorded at">{recorded}</Fact>
        <Fact label="Source">{sourceLabel(row.source)}</Fact>
        <Fact label="Quantity" accent>{info.value}</Fact>
      </div>
      <TechnicalEvidence row={row}/>
    </div>;
  }

  if(row.action==='update'&&fields.length>0){
    return <div className="audit-record-business">
      <div className="audit-facts-grid">
        <Fact label={fields.length===1?'Changed field':'Changed fields'}>{fields.map(human).join(', ')}</Fact>
        {fields.length===1&&<><Fact label="Before">{simpleValue(row.before_value?.[fields[0]])}</Fact><Fact label="After" accent>{simpleValue(row.after_value?.[fields[0]])}</Fact></>}
        {row.lead_name&&<Fact label="Lead">{row.lead_name}</Fact>}
      </div>
      <TechnicalEvidence row={row}/>
    </div>;
  }

  return <div className="audit-record-business">
    <div className="audit-facts-grid">
      <Fact label="Change">{row.action==='insert'?'Created':row.action==='delete'?'Deleted':human(row.action)}</Fact>
      <Fact label="Record">{human(row.entity_type).replace(/s$/,'')}</Fact>
      {row.lead_name&&<Fact label="Lead">{row.lead_name}</Fact>}
      <Fact label="Source">{sourceLabel(row.source)}</Fact>
    </div>
    <TechnicalEvidence row={row}/>
  </div>;
}

export default function AdminAuditPage(){
  const {role,workspace}=useAuth();
  const [rows,setRows]=useState([]),[error,setError]=useState(''),[loading,setLoading]=useState(true),[filter,setFilter]=useState(''),[expanded,setExpanded]=useState(new Set());

  const load=useCallback(async()=>{
    setLoading(true);setError('');
    const {data,error:e}=await supabase.rpc('get_admin_audit_history_v3',{p_workspace_id:workspace.id,p_limit:500});
    if(e)setError(e.message);else setRows(data||[]);
    setLoading(false);
  },[workspace.id]);
  useEffect(()=>{if(canSeeAdmin(role))load()},[role,load]);

  const groups=useMemo(()=>{
    const map=new Map();
    for(const row of rows){
      const key=row.action_group_id||row.id;
      if(!map.has(key))map.set(key,{id:key,rows:[]});
      map.get(key).rows.push(row);
    }
    return [...map.values()].map(group=>{
      group.rows.sort((a,b)=>new Date(a.created_at)-new Date(b.created_at));
      group.created_at=group.rows.reduce((latest,r)=>new Date(r.created_at)>new Date(latest)?r.created_at:latest,group.rows[0].created_at);
      group.title=groupTitle(group.rows);
      group.value=groupValue(group.rows);
      group.actor=group.rows.find(r=>r.actor_name||r.actor_email||r.actor_id);
      group.source=group.rows.find(r=>r.source)?.source||'system';
      group.lead_id=group.rows.find(r=>r.lead_id)?.lead_id||null;
      group.lead_name=group.rows.find(r=>r.lead_name)?.lead_name||null;
      return group;
    }).sort((a,b)=>new Date(b.created_at)-new Date(a.created_at));
  },[rows]);

  if(!canSeeAdmin(role))return <Navigate to="/" replace/>;

  const q=filter.trim().toLowerCase();
  const shown=groups.filter(g=>{
    if(!q)return true;
    const haystack=[g.title,g.source,g.actor?.actor_name,g.actor?.actor_email,g.lead_name,...g.rows.flatMap(r=>[r.entity_type,r.action,r.field_name,r.after_value?.event_type])].filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(q);
  });

  const setAll=open=>setExpanded(open?new Set(shown.map(g=>g.id)):new Set());
  const toggle=(id,isOpen)=>setExpanded(prev=>{const next=new Set(prev);if(isOpen)next.add(id);else next.delete(id);return next;});

  return <section className="page-stack">
    <div><h1 className="page-title">Audit / Change History</h1><p className="page-description">Business changes first. Technical evidence is available when you need it.</p></div>
    <div className="panel audit-toolbar">
      <input className="text-input audit-search" value={filter} onChange={e=>setFilter(e.target.value)} placeholder="Search action, Lead, person, source or field…"/>
      <button className="secondary-button" onClick={()=>setAll(false)}>Collapse all</button>
      <button className="secondary-button" onClick={()=>setAll(true)}>Expand all</button>
      <button className="secondary-button" onClick={load} disabled={loading}>{loading?'Refreshing…':'Refresh'}</button>
      <span className="pill">{shown.length} actions</span>
    </div>
    {error&&<div className="error-message">{error}</div>}
    <div className="audit-groups">{loading?<div className="panel empty-state">Loading Change History…</div>:shown.length===0?<div className="panel empty-state"><strong>No matching history.</strong></div>:shown.map(group=>{
      const actor=group.actor?.actor_name||group.actor?.actor_email||(group.actor?.actor_id?'CRM user':'System');
      return <details className="panel audit-business-card" key={group.id} open={expanded.has(group.id)} onToggle={e=>toggle(group.id,e.currentTarget.open)}>
        <summary className="audit-business-summary">
          <div className="audit-business-main">
            <strong>{group.title}</strong>
            <span>{dateTimeFmt.format(new Date(group.created_at))} · {actor} · {sourceLabel(group.source)}{group.lead_name?` · ${group.lead_name}`:''}</span>
          </div>
          <div className="audit-business-value">
            {group.value.primary&&<strong>{group.value.primary}</strong>}
            {group.value.secondary&&<small>{group.value.secondary}</small>}
            {group.rows.length>1&&<span className="pill">{group.rows.length} changes</span>}
            {group.lead_id&&<Link className="text-action" to={`/leads/${group.lead_id}`} onClick={e=>e.stopPropagation()}>Open Lead</Link>}
          </div>
        </summary>
        <div className="audit-business-body">{group.rows.map(row=><AuditRecord key={row.id} row={row}/>)}</div>
      </details>;
    })}</div>
    <div className="small-note">Admins can inspect up to the 500 most recent persisted changes. Revert remains an operational action on the Lead Record.</div>
  </section>;
}
